import { UseDisclosureReturn, useMediaQuery } from "@chakra-ui/react"
import { useMergedDisclosure } from "./utils"

type PanelSize = "xs" | "sm" | "md"

// Use these drawer sizes based on media queries.
// Note: using useMediaQuery instead of useBreakpointValue for
// more control over the breakpoints.
// If desired, this could be refactored to a reusable hook that
// accepts a breakpoint map.
export function usePanelSize(options?: {
  size?: PanelSize
  allowOverlay?: boolean
}) {
  const size = options?.size || "md"
  const allowOverlay = options?.allowOverlay || false

  // Media query breakpoints.
  const lg = "(min-width: 1001px)"
  const md = "(min-width: 501px)"
  const sm = "(min-width: 0px)"

  // Order mattes; largest screensize to smallest.
  const breakpointsMap: Record<PanelSize, Record<string, string>> = {
    xs: { [lg]: "xs", [md]: "xs", [sm]: allowOverlay ? "full" : "xs" },
    sm: { [lg]: "sm", [md]: "xs", [sm]: allowOverlay ? "full" : "xs" },
    md: { [lg]: "md", [md]: "sm", [sm]: allowOverlay ? "full" : "xs" }
  }

  // Get the breakpoints (list of booleans).
  const breakpoints = useMediaQuery(Object.keys(breakpointsMap[size]))

  // Get the index of the first breakpoint that is true.
  // (This is why order matters for breakpointsMap.)
  const breakpointIndex = breakpoints.findIndex((breakpoint) => breakpoint)

  // The breakpointIndex corresponds to the value in breakpointsMap.
  const breakpointValue = Object.values(breakpointsMap[size])[breakpointIndex]

  return breakpointValue
}

export type UseAugmentationPanelProps = {
  // Offset from all sides of screen, in pixels.
  gutter?: number
  offset?: {
    top?: number
    right?: number
    bottom?: number
  }
  allowOverlay?: boolean
  size?: PanelSize
  disclosure?: UseDisclosureReturn
}

// TODO: support opening panel from the left side as well.
function useAugmentationPanel(props: UseAugmentationPanelProps) {
  const { offset, allowOverlay, size: panelSizeToken } = props
  const gutter = props.gutter || 10

  const size = usePanelSize({ allowOverlay, size: panelSizeToken })

  const { isOpen, onClose } = useMergedDisclosure(props.disclosure, {
    isOpen: props.disclosure?.isOpen
  })

  const baseOffset = {
    top: `${(offset?.top || 0) + gutter}px`,
    bottom: `${(offset?.bottom || 0) + gutter}px`
  }

  // Styling the drawer content conditionally based on drawer size.
  const contentSizeProps =
    // Use overlay styles for when drawer is "full" size.
    size === "full"
      ? {
          maxW: `calc(100% - ${gutter * 2}px)`,
          maxH: `calc(100% - ${baseOffset.top} - ${baseOffset.bottom})`,
          inset: `${gutter}px !important`,
          top: `${baseOffset.top} !important`,
          bottom: `${baseOffset.bottom} !important`
        }
      : {
          right: `${gutter}px !important`,
          top: `${baseOffset.top} !important`,
          bottom: `${baseOffset.bottom} !important`
        }

  function getDrawerProps() {
    return {
      isOpen: isOpen,
      onClose: onClose,
      variant: "aside",
      size: size,
      trapFocus: false,
      blockScrollOnMount: false,
      closeOnOverlayClick: false
    }
  }

  function getDrawerContentProps() {
    return {
      className: "AugmentationPanel",
      marginRight: offset?.right ? `${offset.right}px` : undefined,
      ...contentSizeProps
    }
  }

  return {
    getDrawerProps,
    getDrawerContentProps
  }
}

export default useAugmentationPanel
